import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

function SectionCard({ title, children, error }) {
  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardHeader
        title={
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", textTransform: "uppercase" }}
          >
            {title}
          </Typography>
        }
        sx={{ pb: 0, px: 2, pt: 2 }}
      />
      <CardContent sx={{ px: 2, pb: 2 }}>
        {children}
        {!!error && (
          <FormHelperText error sx={{ mt: 1 }}>
            {error}
          </FormHelperText>
        )}
      </CardContent>
    </Card>
  );
}

export default function QcServiceDetailsSection({
  mode = "edit",
  natureOfWork,
  natureOfWorkOthersText,
  onNatureOfWorkChange,
  onNatureOfWorkOthersTextChange,

  structureType,
  structureNatureOfBusiness,
  structureOthersText,
  onStructureTypeChange,
  onStructureNatureOfBusinessChange,
  onStructureOthersTextChange,

  gpcMain,
  gpcInsects,
  gpcOtherText,
  onGpcMainChange,
  onGpcInsectChange,
  onGpcOtherTextChange,

  actionTaken,
  actionTakenOthersText,
  onActionTakenChange,
  onActionTakenOthersTextChange,

  errors = {},
}) {
  const readOnly = mode === "view";

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SectionCard
          title="Nature of Work"
          error={errors?.nature_of_work_count?.message}
        >
          <FormControl fullWidth error={!!errors?.nature_of_work_count}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={natureOfWork.generalPestControl}
                    onChange={onNatureOfWorkChange("generalPestControl")}
                    disabled={readOnly}
                  />
                }
                label="General Pest Control"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={natureOfWork.disinfection}
                    onChange={onNatureOfWorkChange("disinfection")}
                    disabled={readOnly}
                  />
                }
                label="Disinfection"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={natureOfWork.fumigation}
                    onChange={onNatureOfWorkChange("fumigation")}
                    disabled={readOnly}
                  />
                }
                label="Fumigation"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={natureOfWork.rodentControl}
                    onChange={onNatureOfWorkChange("rodentControl")}
                    disabled={readOnly}
                  />
                }
                label="Rodent Control"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={natureOfWork.foggingMisting}
                    onChange={onNatureOfWorkChange("foggingMisting")}
                    disabled={readOnly}
                  />
                }
                label="Fogging / Misting"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={natureOfWork.others}
                    onChange={onNatureOfWorkChange("others")}
                    disabled={readOnly}
                  />
                }
                label="Others"
              />
            </FormGroup>

            {natureOfWork.others && (
              <TextField
                label="Please specify"
                value={natureOfWorkOthersText}
                onChange={(e) => onNatureOfWorkOthersTextChange(e.target.value)}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                disabled={readOnly}
                error={!!errors?.nature_of_work_others_text}
                helperText={errors?.nature_of_work_others_text?.message || " "}
              />
            )}
          </FormControl>
        </SectionCard>
      </Grid>

      <Grid item xs={12}>
        <SectionCard
          title="Type of Structure"
          error={errors?.structure_type_count?.message}
        >
          <FormControl fullWidth error={!!errors?.structure_type_count}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={structureType.residential}
                    onChange={onStructureTypeChange("residential")}
                    disabled={readOnly}
                  />
                }
                label="Residential"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={structureType.commercialEstablishment}
                    onChange={onStructureTypeChange("commercialEstablishment")}
                    disabled={readOnly}
                  />
                }
                label="Commercial Establishment"
              />
              {structureType.commercialEstablishment && (
                <TextField
                  label="Nature of Business"
                  value={structureNatureOfBusiness}
                  onChange={(e) => onStructureNatureOfBusinessChange(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1, mb: 1 }}
                  disabled={readOnly}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={structureType.others}
                    onChange={onStructureTypeChange("others")}
                    disabled={readOnly}
                  />
                }
                label="Others"
              />
              {structureType.others && (
                <TextField
                  label="Please specify"
                  value={structureOthersText}
                  onChange={(e) => onStructureOthersTextChange(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={readOnly}
                />
              )}
            </FormGroup>
          </FormControl>
        </SectionCard>
      </Grid>

      <Grid item xs={12}>
        <SectionCard
          title="General Pest Control"
          error={errors?.gpc_count?.message}
        >
          <FormControl fullWidth error={!!errors?.gpc_count}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gpcMain.insects}
                    onChange={onGpcMainChange("insects")}
                    disabled={readOnly}
                  />
                }
                label="Flying and Crawling Insects"
              />

              {gpcMain.insects && (
                <Box sx={{ ml: 3, mt: 0.5, mb: 1 }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={gpcInsects.cockroach}
                          onChange={onGpcInsectChange("cockroach")}
                          disabled={readOnly}
                        />
                      }
                      label="Cockroach"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={gpcInsects.flies}
                          onChange={onGpcInsectChange("flies")}
                          disabled={readOnly}
                        />
                      }
                      label="Flies"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={gpcInsects.mosquitoes}
                          onChange={onGpcInsectChange("mosquitoes")}
                          disabled={readOnly}
                        />
                      }
                      label="Mosquitoes"
                    />
                  </FormGroup>
                </Box>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={gpcMain.rodents}
                    onChange={onGpcMainChange("rodents")}
                    disabled={readOnly}
                  />
                }
                label="Rodents"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={gpcMain.other}
                    onChange={onGpcMainChange("other")}
                    disabled={readOnly}
                  />
                }
                label="Other Pests"
              />

              {gpcMain.other && (
                <TextField
                  label="Please specify"
                  value={gpcOtherText}
                  onChange={(e) => onGpcOtherTextChange(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={readOnly}
                />
              )}
            </FormGroup>
          </FormControl>
        </SectionCard>
      </Grid>

      <Grid item xs={12}>
        <SectionCard
          title="Action Taken / Treatment Done"
          error={errors?.action_taken_count?.message}
        >
          <FormControl fullWidth error={!!errors?.action_taken_count}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.mistSpraying}
                    onChange={onActionTakenChange("mistSpraying")}
                    disabled={readOnly}
                  />
                }
                label="Mist Spraying"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.thermalFogging}
                    onChange={onActionTakenChange("thermalFogging")}
                    disabled={readOnly}
                  />
                }
                label="Thermal Fogging"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.coldFogging}
                    onChange={onActionTakenChange("coldFogging")}
                    disabled={readOnly}
                  />
                }
                label="Cold Fogging"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.larviciding}
                    onChange={onActionTakenChange("larviciding")}
                    disabled={readOnly}
                  />
                }
                label="Larviciding"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.gelBaiting}
                    onChange={onActionTakenChange("gelBaiting")}
                    disabled={readOnly}
                  />
                }
                label="Gel Baiting"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.uvLight}
                    onChange={onActionTakenChange("uvLight")}
                    disabled={readOnly}
                  />
                }
                label="UV Light"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.wiping}
                    onChange={onActionTakenChange("wiping")}
                    disabled={readOnly}
                  />
                }
                label="Wiping"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.steaming}
                    onChange={onActionTakenChange("steaming")}
                    disabled={readOnly}
                  />
                }
                label="Steaming"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actionTaken.others}
                    onChange={onActionTakenChange("others")}
                    disabled={readOnly}
                  />
                }
                label="Others"
              />
            </FormGroup>

            {actionTaken.others && (
              <TextField
                label="Please specify"
                value={actionTakenOthersText}
                onChange={(e) => onActionTakenOthersTextChange(e.target.value)}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                disabled={readOnly}
                error={!!errors?.action_taken_others_text}
                helperText={errors?.action_taken_others_text?.message || " "}
              />
            )}
          </FormControl>
        </SectionCard>
      </Grid>
    </Grid>
  );
}